import { Button, Result } from 'antd';

export interface FullPageErrorProps {
  /** Error title */
  title?: string;
  /** Error message/description */
  message?: string;
  /** Callback when user clicks retry button */
  onRetry?: () => void;
  /** Custom retry button text */
  retryText?: string;
}

/**
 * Full Page Error Component
 *
 * Displays a full-screen error message with optional retry functionality.
 * Use this for critical application errors that prevent normal operation.
 *
 * @example
 * ```tsx
 * <FullPageError
 *   title="Unable to load application"
 *   message="Failed to connect to the server."
 *   onRetry={() => window.location.reload()}
 * />
 * ```
 */
export function FullPageError({
  title = 'Unable to load application',
  message = 'Failed to connect to the server. Please check your connection and try again.',
  onRetry,
  retryText = 'Try Again',
}: FullPageErrorProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Result
        status="error"
        title={title}
        subTitle={<span style={{ whiteSpace: 'pre-line' }}>{message}</span>}
        extra={
          onRetry && (
            <Button type="primary" onClick={onRetry}>
              {retryText}
            </Button>
          )
        }
      />
    </div>
  );
}
